import Intl from '@fx-ui/intl';
import { PlatformVipAddOnKey, VipAddOnKey } from '~/interfaces';
import type CorpUserModel from '~/models/corpUser';
import { CURRENCY_SIGN, ECode, helpSourceIntl, VIP_ERROR } from '~/constants';
import { get } from '@libs/shared';
import { hasFunctionAILab } from '~/utils/site';

/**
 * 根据error code获取相关文案
 * @param root0
 * @param root0.code 错误码
 * @param root0.corpUserStore 企业信息
 */
export const getVipErrMsg = ({
    code,
    corpUserStore
}: {
    code: number | string | symbol;
    corpUserStore: CorpUserModel;
}): {
    title?: string;
    msg?: string;
    memMsg?: string;
    doc?: string;
    allowAddon?: boolean;
    // 是否是增购用量（与之相对的是增购功能）
    isAddonUsage?: boolean;
} => {
    const hasSysAdmin = corpUserStore.hasSysAdmin;

    /**
     * 获取超量相关文案
     * @param name 功能数量文案
     * @param action 建议用户行为
     */
    const getOverLimitMsg = (name: string, action: string) => {
        const intlParams = {
            featureName: name,
            action: action
        };

        const msg = (() => {
            if (hasSysAdmin) {
                return Intl.get('fxp.base.quota_overrun_buy.tip', intlParams);
            }

            return Intl.get('fxp.base.quota_overrun_admin_buy.tip', intlParams);
        })();

        return {
            isAddonUsage: true,
            title: Intl.get('fxp.base.quota_buy_reminder.ttl'),
            msg
        };
    };

    const getUpgradeTitle = (allowAddon: boolean) => {
        return allowAddon
            ? Intl.get('fxp.base.feature_buy_remind.ttl')
            : Intl.get('fxp.base.plan_upgrade_remind.ttl');
    };

    const getUpgradeMsg = (functionName: string, allowAddon: boolean) => {
        const intlParams = {
            featureName: functionName
        };
        if (hasSysAdmin) {
            return allowAddon
                ? Intl.get('fxp.base.feature_unsupported_buy.tip', intlParams)
                : Intl.get('fxp.base.feature_unsupported_upgrade.tip', intlParams);
        }
        return allowAddon
            ? Intl.get('fxp.base.feature_unsupported_admin.tip', intlParams)
            : Intl.get('fxp.base.feature_upgrade_admin.tip', intlParams);
    };

    switch (code) {
        /**
         * 以下是该功能用量超限的提示
         * @see FXD-61380
         */
        case VIP_ERROR.CORP_COOP_DOCKER: {
            const allowAddon = corpUserStore.allowAddonInPlatform(
                PlatformVipAddOnKey.corp_coop_docker
            );
            const name = Intl.get('vip.function.count.corpCoopDocker');
            const action = Intl.get('vip.function.action.corpCoopDocker');
            return {
                ...getOverLimitMsg(name, action),
                allowAddon
            };
        }
        case VIP_ERROR.APP: {
            const allowAddon = corpUserStore.allowAddonInAppBuilder(VipAddOnKey.app);
            const name = Intl.get('vip.function.count.app');
            const action = Intl.get('vip.function.action.app');
            return {
                ...getOverLimitMsg(name, action),
                allowAddon
            };
        }
        case VIP_ERROR.CORP_COOP_LIMIT: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.corp_coop);
            const name = Intl.get('vip.function.count.corpCoop');
            const action = Intl.get('vip.function.action.corpCoop');
            return {
                ...getOverLimitMsg(name, action),
                allowAddon
            };
        }
        case VIP_ERROR.USER_SYNC: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.user_sync);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('vip.center.userSync'), allowAddon),
                doc: helpSourceIntl.get('doc_vip_9138'),
                allowAddon
            };
        }
        case VIP_ERROR.CUSTOM_LOGIN: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.custom_login);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('custom.login'), allowAddon),
                doc: helpSourceIntl.get('doc_vip_11415'),
                allowAddon
            };
        }
        case VIP_ERROR.SSO: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.sso);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('sso'), allowAddon),
                doc: helpSourceIntl.get('ope_sso'),
                allowAddon
            };
        }
        case VIP_ERROR.CORP_THEME: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.corp_theme);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('corp.theme'), allowAddon),
                doc: helpSourceIntl.get('doc_corp_theme'),
                allowAddon
            };
        }
        case VIP_ERROR.CONTACT_MEMBER_STOP: {
            const allowAddon = corpUserStore.allowAddonInPlatform(
                PlatformVipAddOnKey.member_freeze
            );
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('vip.member.freeze'), allowAddon),
                doc: helpSourceIntl.get('doc_org_content'),
                allowAddon
            };
        }
        case VIP_ERROR.CORP_COOP: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.corp_coop);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('corp.coop'), allowAddon),
                doc: helpSourceIntl.get('doc_collaborator_guide'),
                allowAddon
            };
        }
        case VIP_ERROR.GLOBAL_WATERMARK: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.watermark);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('fxp.base.bg.ver_info.corp_watermark.ttl'), allowAddon),
                doc: helpSourceIntl.get('doc_watermark'),
                allowAddon
            };
        }
        case VIP_ERROR.CORP_EXECUTIVE: {
            const allowAddon = corpUserStore.allowAddonInPlatform(
                PlatformVipAddOnKey.corp_executive
            );
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('corp.executive'), allowAddon),
                allowAddon,
                doc: helpSourceIntl.get('corp_executive')
            };
        }
        case ECode.OPEN_PLATFORM_UNAVAILABLE:
        case VIP_ERROR.OPEN_PLATFORM: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.open);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('openPlatform'), allowAddon),
                doc: helpSourceIntl.get('open_platform'),
                allowAddon
            };
        }
        case VIP_ERROR.OPEN_API: {
            const allowAddon =
                corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.data_api) ||
                corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.log_api) ||
                corpUserStore.allowAddonInAppBuilder(VipAddOnKey.data_api);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('order.add.api'), allowAddon),
                doc: helpSourceIntl.get('open_api'),
                allowAddon
            };
        }
        case VIP_ERROR.PRIVATE_PLUGIN: {
            const allowAddon = corpUserStore.allowAddonInPlatform(
                PlatformVipAddOnKey.open_private_plugin
            );
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('openPlatform.unit.private'), allowAddon),
                doc: helpSourceIntl.get('private_plugin'),
                allowAddon
            };
        }
        case VIP_ERROR.OPEN_APPLICATION: {
            const allowAddon = corpUserStore.allowAddonInPlatform(
                PlatformVipAddOnKey.open_application
            );
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(
                    Intl.get('fxp.base.bg.prod_center.open_application.nm'),
                    allowAddon
                ),
                doc: helpSourceIntl.get('open_application'),
                allowAddon
            };
        }
        case VIP_ERROR.MEMBER_API: {
            const allowAddon = corpUserStore.allowAddonInPlatform(PlatformVipAddOnKey.member_api);
            return {
                title: getUpgradeTitle(allowAddon),
                msg: getUpgradeMsg(Intl.get('fxp.base.open.ai_access.ttl'), allowAddon),
                allowAddon,
                doc: helpSourceIntl.get('member_api')
            };
        }
        default:
            return {};
    }
};

/**
 * 获取增购动作文案
 * @param allowAddon 是否允许增购
 * @param isAddonUsage 是否是增购用量
 */
export const getAddonActionText = (allowAddon = false, isAddonUsage = false) => {
    if (allowAddon) {
        if (isAddonUsage) {
            return Intl.get('fxp.base.buy_more.btn');
        }
        return Intl.get('vip.purchase.yes');
    }
    return Intl.get('fxp.base.upgrade_now.btn');
};

/**
 * 获取货币符号
 */
export const getCurrencySign = () => {
    const currency = get(window, 'jdy_config.currency');
    return get(CURRENCY_SIGN, currency, CURRENCY_SIGN.cny);
};

/**
 * 获取金额文案（文案前拼接货币符号￥/$）
 *
 * @param amount 金额
 */
export const getAmountWithSign = (amount: string | number = '') => {
    return getCurrencySign() + amount;
};

const canAccessAiLab = (corpUser: CorpUserModel) => {
    return (
        corpUser.hasPlatformVipFunction(PlatformVipAddOnKey.ai_lab) || // 套餐包含AI实验室
        corpUser.isAILabBetaEnabled // AI实验室内测用户
    );
};

export const canConfigAiLab = (corpUser: CorpUserModel) => {
    return (
        hasFunctionAILab() && // 版本包含AI实验室（jodoo不包含）
        canAccessAiLab(corpUser)
    );
};

export const canUseAiLab = (corpUser: CorpUserModel) => {
    return (
        canConfigAiLab(corpUser) && corpUser.isAILabEnabled // 开启了AI实验室
    );
};
